/**
 * Outlook OAuth Step
 * Step 2B: Connect Outlook via OAuth
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/base/buttons/button";
import { ArrowLeft, Shield01, Check } from "@untitledui/icons";
import { useCreateEmailAccount, useGetOutlookAuthUrl } from "@/hooks/api/use-email-accounts";
import { toast } from "@/lib/toast-service";
import { EmailProvider } from "@/types/api/email";

interface OutlookOAuthStepProps {
    email: string;
    displayName?: string;
    onBack: () => void;
    onSuccess: () => void;
}

export const OutlookOAuthStep = ({
    email,
    displayName,
    onBack,
}: OutlookOAuthStepProps) => {
    const { t } = useTranslation(["pages:email-settings"]);
    const [isConnecting, setIsConnecting] = useState(false);

    const createAccountMutation = useCreateEmailAccount();
    const getAuthUrlMutation = useGetOutlookAuthUrl();

    const handleConnect = async () => {
        setIsConnecting(true);

        try {
            // Step 1: Create the email account record
            const account = await createAccountMutation.mutateAsync({
                email_address: email,
                provider: EmailProvider.OUTLOOK,
                display_name: displayName || undefined,
            });

            // Step 2: Get the OAuth authorization URL
            const authResponse = await getAuthUrlMutation.mutateAsync(account.account_id);

            // Step 3: Redirect to Microsoft OAuth
            // The backend will redirect back to frontend after OAuth completion
            window.location.href = authResponse.authorization_url;
        } catch (error) {
            setIsConnecting(false);
            toast.error(t("errors.createAccount"));
        }
    };

    const permissions = [
        t("outlookOAuth.permissionRead"),
        t("outlookOAuth.permissionModify"),
    ];

    return (
        <div className="space-y-6">
            {/* Title */}
            <div>
                <h3 className="text-base font-medium text-primary">
                    {t("outlookOAuth.title")}
                </h3>
                <p className="mt-1 text-sm text-tertiary">
                    {t("outlookOAuth.description")}
                </p>
            </div>

            {/* Account Info */}
            <div className="flex items-center gap-3 rounded-lg bg-secondary p-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-tertiary">
                    <Shield01 className="h-5 w-5 text-fg-brand-primary" />
                </div>
                <div>
                    <p className="font-medium text-primary">
                        {t("outlookOAuth.secureAccess")}
                    </p>
                    <p className="text-sm text-tertiary">{email}</p>
                </div>
            </div>

            {/* Permissions List */}
            <div className="space-y-3">
                <p className="text-sm font-medium text-primary">
                    {t("outlookOAuth.permissionsTitle")}
                </p>
                <ul className="space-y-2">
                    {permissions.map((permission, index) => (
                        <li key={index} className="flex items-center gap-2 text-sm text-tertiary">
                            <Check className="h-4 w-4 text-fg-success-secondary" />
                            {permission}
                        </li>
                    ))}
                </ul>
            </div>

            {/* Microsoft Sign In Button */}
            <Button
                onClick={handleConnect}
                isDisabled={isConnecting}
                isLoading={isConnecting}
                color="primary"
                size="lg"
                className="w-full"
                iconLeading={
                    !isConnecting ? (
                        <svg className="h-5 w-5" viewBox="0 0 24 24">
                            <path fill="#F25022" d="M1 1h10v10H1z" />
                            <path fill="#00A4EF" d="M1 13h10v10H1z" />
                            <path fill="#7FBA00" d="M13 1h10v10H13z" />
                            <path fill="#FFB900" d="M13 13h10v10H13z" />
                        </svg>
                    ) : undefined
                }
            >
                {isConnecting ? t("outlookOAuth.connecting") : t("outlookOAuth.signInButton")}
            </Button>

            {/* Redirect Note */}
            <p className="text-center text-xs text-tertiary">
                {t("outlookOAuth.redirectNote")}
            </p>

            {/* Back Button */}
            <div className="flex justify-start pt-2">
                <Button
                    onClick={onBack}
                    isDisabled={isConnecting}
                    color="tertiary"
                    size="sm"
                    iconLeading={ArrowLeft}
                >
                    {t("actions.back")}
                </Button>
            </div>
        </div>
    );
};
